import { AxiosError, Method } from 'axios';

import { shouldRaiseAAL } from '../Auth/Auth';

import { walletEntryDTO } from './helpers';

import { getHeadersForSSRRequest } from 'businessLogic/utils';
import HttpClient from 'server/helpers/HttpClient';
import { insightSelectors } from 'store/insight/selectors';
import { RootState } from 'store/store';
import { UserWallet, WalletItemType } from 'types/Wallet';
import { CardType } from 'types/constants';

const httpClient = HttpClient.getInstance();

/**
 * Exchanges a Plaid public token for a wallet token via direct rest2/tokens call.
 * Use this function instead of instantiating Wallet when you only need the exchange.
 */
export async function exchangePlaidToken(
  store: RootState,
  params: { publicToken: string }
): Promise<{ id: string }> {
  const {
    insight,
    config: { ssrtid, endpoints, credentials },
  } = store;
  const token = insightSelectors.tokenSelector(insight) as string;
  const walletApiEndpoint = `${endpoints.wallet.rest2}/tokens`;

  const response = await httpClient({
    url: walletApiEndpoint,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    params: { intuit_apikey: credentials.browserApiKey },
    endpoint: walletApiEndpoint,
    ssrtid,
    token,
    isRetryableOnReqTimeout: true,
    data: {
      itemType: 'PLAID',
      lifetime: 30240,
      plaid: { publicToken: params.publicToken },
    },
    withCredentials: false,
    event: 'wallet',
  });

  const id = response?.data?.id;
  if (!id || typeof id !== 'string') {
    throw new Error('Invalid or missing wallet token in Plaid exchange response');
  }
  return { id };
}

type RequestOptions =
  | {
      card: {
        address: { addressComponents: Array<{ name: string; value: string }> };
        cvc: string;
        default: false;
        expMonth: string;
        expYear: string;
        isSavePaymentMethodChecked: false;
        name: string;
        number: number;
        cardType: CardType;
        savePaymentMethod?: boolean;
      };
      itemType: WalletItemType;
    }
  | {
      bankAccount: {
        address: { addressComponents: Array<{ name: string; value: string }> };
        accountNumber: string;
        accountNumberConfirm: string;
        accountType: string;
        bankCode: string;
        branchCode?: string;
        country?: string;
        name: string;
        phone: string;
      };
      itemType: WalletItemType;
      multiUse?: boolean;
    }
  | {};
type walletHttpRequestOptions = {
  reqType: string;
  endpoint: string;
  method: Method;
  data?: RequestOptions;
  store: RootState;
  headers?: Record<string, string>;
};
const baseUrl = 'rest/wallets';

export const ERROR_FRICTIONLESS_LOGIN_OTP_REMEDIATION =
  'Frictinless Login remediation - OTP elevation needed';

export default class Wallet {
  constructor() {}

  private async walletSSRRequest({ endpoint, method, data = {}, store }: walletHttpRequestOptions) {
    const headers = getHeadersForSSRRequest(store, {
      Accept: 'application/json, text/javascript, */*; q=0.01',
    });
    const {
      insight,
      config: { portal, ssrtid },
    } = store;
    const token = insightSelectors.tokenSelector(insight) as string;

    const ssrWalletsEndpoint = `/${portal}/${endpoint}`;

    const response = await httpClient({
      url: `${ssrWalletsEndpoint}/`,
      method,
      headers,
      endpoint: ssrWalletsEndpoint,
      ssrtid,
      token,
      data,
      event: 'wallet',
    });
    return response.data;
  }

  private async walletApiRequest({ method, data, store, endpoint }: walletHttpRequestOptions) {
    const {
      insight,
      config: { ssrtid, endpoints, credentials },
    } = store;
    const token = insightSelectors.tokenSelector(insight) as string;
    const walletApiEndpoint = `${endpoints.wallet.rest2}/${endpoint}`;

    const response = await httpClient({
      url: walletApiEndpoint,
      method,
      headers: {
        'Content-Type': 'application/json',
      },
      params: {
        intuit_apikey: credentials.browserApiKey,
      },
      endpoint: walletApiEndpoint,
      ssrtid,
      token,
      isRetryableOnReqTimeout: true,
      data,
      withCredentials: false, // To prevent a 401 error for a non logged-in user that has some user cookies (was logged-in but then logged-out)
      event: 'wallet',
    });
    return response.data;
  }

  async fetchUserWallets(store: RootState) {
    try {
      return await this.walletSSRRequest({
        reqType: 'fetchUserWallets',
        endpoint: baseUrl,
        method: 'GET',
        store,
      });
    } catch (error) {
      if (shouldRaiseAAL(error as AxiosError)) {
        throw new Error(ERROR_FRICTIONLESS_LOGIN_OTP_REMEDIATION);
      }
      throw error;
    }
  }

  async updateUserWallet(store: RootState, userWallet: UserWallet) {
    const { id, walletType } = userWallet;

    const walletUpdateRes = await this.walletSSRRequest({
      reqType: 'updateUserWallet',
      endpoint: `${baseUrl}/${walletType}/${id}`,
      method: 'PATCH',
      data: {
        walletUpdatedProps: userWallet,
      },
      store,
    });

    return walletUpdateRes;
  }

  async deleteUserWallet(store: RootState, userWallet: Partial<UserWallet>) {
    const { id, walletType } = userWallet;

    const walletDeleteRes = await this.walletSSRRequest({
      reqType: 'deleteUserWallet',
      endpoint: `${baseUrl}/${walletType}/${id}`,
      method: 'DELETE',
      store,
    });

    return walletDeleteRes;
  }

  async createUserWallet(
    store: RootState,
    params: {
      walletType: 'card' | 'bank';
      authEntityId: string;
      shortLivedToken: string;
    }
  ) {
    const { id } = await this.walletSSRRequest({
      reqType: 'createUserWallet',
      endpoint: `${baseUrl}/${params.walletType}`,
      method: 'POST',
      data: {
        authEntityId: params.authEntityId,
        shortLivedToken: params.shortLivedToken,
      },
      store,
    });
    return { id };
  }

  async createShortLivedToken(
    store: RootState,
    paymentData: {
      ccDetails?: {
        number: string;
        cardType: string;
        name: string;
        expYear: string;
        expMonth: string;
        address: {
          postalCode: string;
        };
        cvc: string;
        default: boolean;
        metadata?: Array<{
          name?: string;
          value?: string;
        }>;
      };
      bankDetails?: {
        accountNumber: string;
        accountNumberConfirm?: string;
        accountType: string;
        bankCode?: string;
        name: string;
        phone: string;
        itemType: string;
        institutionNumber?: string;
        transitNumber?: string;
        metadata?: Array<{
          name?: string;
          value?: string;
        }>;
      };
      paymentMethodType?: string;
    } = {}
  ) {
    const { ccDetails, paymentMethodType } = paymentData;
    let { bankDetails } = paymentData;
    if (paymentMethodType === 'eft') {
      bankDetails = walletEntryDTO.convertFromCPFormat.covertToEftWalletRequestFormat(bankDetails);
    }
    let tokenizationPayload;
    tokenizationPayload = {
      itemType: ccDetails ? 'CARD' : 'BANK_ACCOUNT',
      ...(ccDetails && { card: ccDetails }),
      ...(bankDetails && { bankAccount: bankDetails }),
      ...{ multiUse: true },
    };

    tokenizationPayload =
      walletEntryDTO.convertFromCPFormat.addAddressComponentForRest2Tokenization(
        tokenizationPayload
      );

    const { id } = await this.walletApiRequest({
      reqType: `token_${tokenizationPayload.itemType}`,
      endpoint: `tokens`,
      method: 'POST',
      store,
      data: tokenizationPayload,
    });

    return { token: id };
  }

  async fetchPlaidLinkToken(
    store: RootState
  ): Promise<{ linkToken: string; plaidReferenceId: string }> {
    const {
      insight,
      config: { ssrtid, endpoints, credentials },
    } = store;
    const token = insightSelectors.tokenSelector(insight) as string;
    const paymentSessionsEndpoint = endpoints.wallet.paymentSessions;

    if (!paymentSessionsEndpoint)
      throw new Error(
        `Missing required config: endpoints.wallet.paymentSessions (received: ${JSON.stringify(
          paymentSessionsEndpoint
        )})`
      );

    const response = await httpClient({
      url: paymentSessionsEndpoint,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      params: { intuit_apikey: credentials?.browserApiKey },
      endpoint: paymentSessionsEndpoint,
      ssrtid,
      token,
      isRetryableOnReqTimeout: true,
      data: { sessionType: 'PLAID' },
      withCredentials: false,
      event: 'wallet',
    });

    const linkToken = response?.data?.sessionData?.linkToken;
    const plaidReferenceId = response?.data?.sessionData?.plaidReferenceId;

    if (!linkToken || typeof linkToken !== 'string') {
      throw new Error('Invalid or missing linkToken in Plaid response');
    }

    if (!plaidReferenceId || typeof plaidReferenceId !== 'string') {
      throw new Error('Invalid or missing plaidReferenceId in Plaid response');
    }

    return { linkToken, plaidReferenceId };
  }
}
